import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { NotFoundView, DefaultRouteError } from "@/components/RouteFallbacks";
import { FaqSection, faqSchema } from "@/components/PageFaq";
import { PASSIVE_BY_SLUG, PASSIVES, type Passive } from "@/lib/passives-data";

function passiveFaq(p: Passive) {
  return [
    { q: `What does ${p.name} do in Palworld?`, a: `${p.name} gives ${p.effect}. ${p.summary}` },
    { q: `Which Pals should have ${p.name} in Palworld?`, a: `${p.bestOn}. ${p.tips[0]}` },
    {
      q: `Is ${p.name} worth breeding in Palworld?`,
      a:
        p.tier === "Negative"
          ? `No — ${p.name} is a negative trait (${p.effect}), so breed it out by keeping offspring that inherit only the traits you want.`
          : `${p.tier}-tier passive. ${p.tips[1]}`,
    },
    p.ask,
  ];
}

export const Route = createFileRoute("/passives/$slug")({
  loader: ({ params }): { passive: Passive } => {
    const passive = PASSIVE_BY_SLUG[params.slug];
    if (!passive) throw notFound();
    return { passive };
  },
  head: ({ loaderData }) => {
    if (!loaderData)
      return {
        meta: [{ title: "Passive not found — Pal Stars" }, { name: "robots", content: "noindex" }],
      };
    const p = loaderData.passive;
    const long = `Palworld ${p.name} Passive: Effect & Best Pals`;
    const title = long.length <= 60 ? long : `Palworld ${p.name} Passive Skill`.slice(0, 60);
    const base = `${p.name} in Palworld — ${p.effect}. Best on ${p.bestOn.toLowerCase()}.`;
    const desc = base.length <= 155 ? base : base.slice(0, 152).replace(/\s+\S*$/, "") + "…";
    const url = `https://palstars.com/passives/${p.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        faqSchema(passiveFaq(p), url),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${url}#article`,
            headline: `Palworld ${p.name} passive skill`,
            description: desc,
            mainEntityOfPage: url,
            author: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/" },
            publisher: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/" },
            keywords: [
              `palworld ${p.name.toLowerCase()}`,
              `palworld ${p.name.toLowerCase()} passive`,
              `best ${p.kind.toLowerCase()} passives palworld`,
            ].join(", "),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": `${url}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://palstars.com/" },
              {
                "@type": "ListItem",
                position: 2,
                name: "Passive Skills",
                item: "https://palstars.com/passives",
              },
              { "@type": "ListItem", position: 3, name: p.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: PassiveDetail,
  errorComponent: DefaultRouteError,
  notFoundComponent: () => (
    <NotFoundView
      title="That passive skill isn't in our database"
      message="No Palworld passive skill matches this URL. Browse the full passive skills list for every trait, its effect value and the Pals it belongs on."
    />
  ),
});

function PassiveDetail() {
  const { passive: p } = Route.useLoaderData();
  const faq = passiveFaq(p);
  const related = PASSIVES.filter((x) => x.kind === p.kind && x.slug !== p.slug).slice(0, 6);

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>{" "}
        /{" "}
        <Link to="/passives" className="hover:text-foreground">
          Passive Skills
        </Link>{" "}
        / <span className="text-foreground">{p.name}</span>
      </nav>

      <span className="text-xs font-semibold uppercase tracking-widest text-primary">
        {p.kind} passive · {p.tier}
      </span>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld {p.name} Passive</h1>
      <p className="aeo-answer mt-3 text-muted-foreground">{p.summary}</p>

      <dl className="mt-6 grid gap-3 sm:grid-cols-2">
        <div className="glass rounded-xl p-4 sm:col-span-2">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Effect</dt>
          <dd className="mt-1 font-semibold">{p.effect}</dd>
        </div>
        <div className="glass rounded-xl p-4">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Type</dt>
          <dd className="mt-1 font-semibold">{p.kind}</dd>
        </div>
        <div className="glass rounded-xl p-4">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Tier</dt>
          <dd className="mt-1 font-semibold">{p.tier}</dd>
        </div>
        <div className="glass rounded-xl p-4 sm:col-span-2">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Best on</dt>
          <dd className="mt-1 font-semibold">{p.bestOn}</dd>
        </div>
      </dl>

      <section className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-xl font-bold">How to use {p.name}</h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          {p.tips.map((t) => (
            <li key={t} className="aeo-answer">
              {t}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm">
          Breed for it with the{" "}
          <Link to="/breeding" className="text-primary hover:underline">
            Palworld breeding calculator
          </Link>
          .
        </p>
      </section>

      <FaqSection items={faq} heading={`${p.name} FAQ`} />

      {related.length > 0 && (
        <nav aria-label={`Other Palworld ${p.kind} passives`} className="glass rounded-2xl p-6 mt-8">
          <h2 className="text-xl font-bold">Other Palworld {p.kind.toLowerCase()} passives</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-sm">
            {related.map((r) => (
              <li key={r.slug}>
                <Link
                  to="/passives/$slug"
                  params={{ slug: r.slug }}
                  className="font-semibold text-primary hover:underline"
                >
                  Palworld {r.name} passive
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm">
            <Link to="/passives" className="text-primary hover:underline">
              Back to the full Palworld passive skills list →
            </Link>
          </p>
        </nav>
      )}
    </div>
  );
}
